import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  createCheckout,
  downloadProcessedFile,
  getUploadStatus,
  uploadEepromFile,
} from '../../eeprom/api';

const toHex = (value) =>
  (Number(value) & 0xff).toString(16).toUpperCase().padStart(2, '0');

const formatHexView = (bytes, bytesPerRow = 16) => {
  const rows = [];
  const hexRowWidth = bytesPerRow * 3 - 1;
  for (let i = 0; i < bytes.length; i += bytesPerRow) {
    const slice = bytes.slice(i, i + bytesPerRow);
    const sliceArray = Array.from(slice);
    const hex = sliceArray.map((b) => toHex(b)).join(' ').padEnd(hexRowWidth, ' ');
    const hexBytes = sliceArray.map((b) => toHex(b));
    const ascii = sliceArray
      .map((b) => (b >= 32 && b <= 126 ? String.fromCharCode(b) : '.'))
      .join('');
    rows.push({
      offset: i.toString(16).toUpperCase().padStart(6, '0'),
      hex,
      hexBytes,
      ascii,
    });
  }
  return rows;
};

const VIN_OFFSET = 0x3a6;
const VIN_LENGTH = 17;

const extractVin = (bytes) => {
  if (!bytes || bytes.length < VIN_OFFSET + VIN_LENGTH) return null;
  const slice = bytes.slice(VIN_OFFSET, VIN_OFFSET + VIN_LENGTH);
  const chars = Array.from(slice).map((b) =>
    b >= 32 && b <= 126 ? String.fromCharCode(b) : ''
  );
  const vin = chars.join('');
  return vin.length === VIN_LENGTH ? vin : null;
};

const getVinYear = (vin) => {
  if (!vin || vin.length < 10) return null;
  const code = vin[9];
  const map = {
    '5': '2005',
    '6': '2006',
    '7': '2007',
    '8': '2008',
    '9': '2009',
  };
  return map[code] || null;
};

const getVinModel = (vin) => {
  if (!vin || vin.length < 5) return null;
  const code = vin.slice(3, 5);
  const map = {
    YS: '360 Modena',
    YT: '360 Spider',
    EW: 'F430',
    EZ: 'F430',
  };
  return map[code] || null;
};

const getVinPlant = (vin) => {
  if (!vin || vin.length < 11) return null;
  const code = vin[10];
  if (code === '0') return 'Maranello';
  return null;
};

const getVinYear360 = (vin) => {
  if (!vin || vin.length < 10) return null;
  const code = vin[9];
  const map = {
    X: '1999',
    Y: '2000',
    '1': '2001',
    '2': '2002',
    '3': '2003',
    '4': '2004',
    '5': '2005',
  };
  return map[code] || null;
};

const F430Cluster = () => {
  const [fileName, setFileName] = useState('');
  const [bytes, setBytes] = useState(null);
  const [upload, setUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [processedLabel, setProcessedLabel] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selections, setSelections] = useState({
    transmission: 'F1',
    speedUnits: 'MPH',
    tempUnits: 'F',
    odometerCorrection: false,
  });
  const { user } = useAuth();

  const rows = useMemo(() => {
    if (!bytes) return [];
    return formatHexView(bytes);
  }, [bytes]);

  const variantSuffix = useMemo(() => {
    const parts = [
      selections.transmission,
      selections.speedUnits,
      selections.tempUnits,
      selections.odometerCorrection ? 'ODO' : null,
    ].filter(Boolean);
    return parts.join('-');
  }, [selections]);

  const newFileName = useMemo(() => {
    if (!upload?.originalFileName) return '';
    const name = upload.originalFileName;
    const dotIndex = name.lastIndexOf('.');
    const baseName = dotIndex >= 0 ? name.slice(0, dotIndex) : name;
    const ext = dotIndex >= 0 ? name.slice(dotIndex) : '.bin';
    const suffix = processedLabel || variantSuffix || 'new';
    return `${baseName}_${suffix}${ext}`;
  }, [upload, processedLabel, variantSuffix]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uploadId =
      params.get('uploadId') ||
      window.localStorage.getItem('eeprom:lastUpload:f430-cluster');
    if (!uploadId || !user) return;
    getUploadStatus({ uploadId })
      .then((result) => {
        setUpload(result);
        setFileName(result.originalFileName || '');
        setIsProcessed(false);
        setProcessedLabel('');
        setHasDownloaded(false);
        setSuccessMessage(
          result.alreadyPaid
            ? 'Payment confirmed. You can process your uploaded file.'
            : 'Payment is still pending.'
        );
      })
      .catch(() => {
        setError('Unable to load upload status.');
      });
  }, [user]);

  const vin = useMemo(() => extractVin(bytes), [bytes]);
  const isVinValid = vin ? vin.startsWith('ZFF') : false;
  const vinModel = useMemo(() => getVinModel(vin), [vin]);
  const vinPlant = useMemo(() => getVinPlant(vin), [vin]);
  const vinYear = useMemo(() => getVinYear(vin), [vin]);
  const vinYear360 = useMemo(() => getVinYear360(vin), [vin]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUpload(null);
    setIsProcessed(false);
    setProcessedLabel('');
    setHasDownloaded(false);
    setSuccessMessage('');
    setError('');

    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result;
      if (!(buffer instanceof ArrayBuffer)) return;
      setBytes(new Uint8Array(buffer));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!user) {
      setError('Please sign in to upload your EEPROM file.');
      return;
    }
    if (!bytes) {
      setError('Please select a file first.');
      return;
    }
    setIsUploading(true);
    setError('');
    setSuccessMessage('');
    try {
      const fileInput = document.querySelector('#f430-cluster-file');
      const file = fileInput?.files?.[0];
      if (!file) {
        throw new Error('Please select a file first.');
      }
      const result = await uploadEepromFile({
        file,
        module: 'f430-cluster',
        selections,
      });
      setUpload(result);
      setIsProcessed(false);
      setProcessedLabel('');
      setHasDownloaded(false);
      window.localStorage.setItem('eeprom:lastUpload:f430-cluster', result.uploadId);
      if (result.alreadyPaid) {
        setSuccessMessage('Payment already on file for this VIN. You can process it now.');
      } else {
        setSuccessMessage('File verified. Continue to payment to download.');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePay = async () => {
    if (!upload?.uploadId) return;
    setIsPaying(true);
    try {
      const { url } = await createCheckout({ uploadId: upload.uploadId });
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
    } finally {
      setIsPaying(false);
    }
  };

  const handleDownload = async () => {
    if (!upload?.uploadId) return;
    try {
      const blob = await downloadProcessedFile({ uploadId: upload.uploadId });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      if (newFileName) {
        anchor.download = newFileName;
      }
      anchor.click();
      window.URL.revokeObjectURL(url);
      setHasDownloaded(true);
      setSuccessMessage(
        'Congratulations! File Downloaded. You may Process the file again if you need to change the options'
      );
    } catch (err) {
      setError(err.message || 'Download failed');
    }
  };

  const handleProcess = () => {
    setProcessedLabel(variantSuffix || 'new');
    setIsProcessed(true);
    setHasDownloaded(false);
    setSuccessMessage('Processing complete. You can download your updated file.');
  };

  const canDownload = Boolean(upload?.alreadyPaid && isProcessed);
  const stepSelected = Boolean(fileName);
  const stepUploaded = Boolean(upload);
  const stepPaid = Boolean(upload?.alreadyPaid);
  const stepProcessed = Boolean(isProcessed);
  const stepDownloaded = Boolean(hasDownloaded);

  return (
    <div className="eeprom-detail">
      <h2>Ferrari 360/F430 Instrument Cluster</h2>
      <p>
        Use the videos below to learn how to remove the cluster and extract the
        EEPROM file. Then click the button to pay and upload your file.
      </p>
      <div className="eeprom-video">
        <iframe
          title="Ferrari F430 cluster removal"
          src="https://www.youtube.com/embed/Jx9vrkyX4R4"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="eeprom-video-placeholder">
        EEPROM extraction video placeholder (link coming soon)
      </div>
      {upload && Number.isFinite(upload.priceCents) && (
        <div className="price-banner">
          Price: ${(upload.priceCents / 100).toFixed(2)}
        </div>
      )}
      {upload?.originalFileName && (
        <div className="file-name-banner">
          Original file: <strong>{upload.originalFileName}</strong>
        </div>
      )}
      {upload?.originalFileName && isProcessed && (
        <div className="file-name-banner">
          New file: <strong>{newFileName}</strong>
        </div>
      )}
      <div className="eeprom-flow">
        <div className="eeprom-actions">
          <label className="file-upload">
            <span className="file-upload-text">Select EEPROM file</span>
            <input
              id="f430-cluster-file"
              type="file"
              accept=".bin,.eep,.hex,.dat"
              onChange={handleFileChange}
            />
          </label>
          <span className={`flow-arrow ${stepSelected ? 'active' : ''}`}>→</span>
          <button type="button" onClick={handleUpload} disabled={isUploading || !bytes}>
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
          <span className={`flow-arrow ${stepUploaded ? 'active' : ''}`}>→</span>
          <button
            type="button"
            onClick={handlePay}
            disabled={!upload || upload.alreadyPaid || isPaying || !Number.isFinite(upload.priceCents)}
          >
            {isPaying
              ? 'Redirecting...'
              : upload && Number.isFinite(upload.priceCents)
              ? `Pay $${(upload.priceCents / 100).toFixed(2)}`
              : 'Pay'}
          </button>
          <span className={`flow-arrow ${stepPaid ? 'active' : ''}`}>→</span>
          <button
            type="button"
            onClick={handleProcess}
            disabled={!upload || !upload.alreadyPaid || isProcessed}
          >
            {isProcessed ? 'Processed' : 'Process'}
          </button>
          <span className={`flow-arrow ${stepProcessed ? 'active' : ''}`}>→</span>
          <div className="download-wrapper">
            <button type="button" onClick={handleDownload} disabled={!canDownload}>
              Download
            </button>
            {upload?.alreadyPaid && <span className="paid-badge">Paid</span>}
          </div>
        </div>
        <div className="eeprom-progress">
          <span className={`progress-icon ${stepSelected ? 'active' : ''}`}>📄</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepUploaded ? 'active' : ''}`}>⬆️</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepPaid ? 'active' : ''}`}>💳</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepProcessed ? 'active' : ''}`}>⚙️</span>
          <span className="progress-spacer" />
          <span className={`progress-icon ${stepDownloaded ? 'active' : ''}`}>⬇️</span>
        </div>
      </div>
      {error && <div className="error-banner">{error}</div>}
      {successMessage && <div className="success-banner">{successMessage}</div>}

      <section className="eeprom-settings">
        <h3>Selections</h3>
        <div className="settings-grid">
          <label>
            Transmission
            <select
              value={selections.transmission}
              onChange={(event) =>
                setSelections((prev) => {
                  setIsProcessed(false);
                  setProcessedLabel('');
                  setHasDownloaded(false);
                  return { ...prev, transmission: event.target.value };
                })
              }
            >
              <option value="F1">F1</option>
              <option value="Gated">Gated</option>
            </select>
          </label>
          <label>
            Speed Units
            <select
              value={selections.speedUnits}
              onChange={(event) =>
                setSelections((prev) => {
                  setIsProcessed(false);
                  setProcessedLabel('');
                  setHasDownloaded(false);
                  return { ...prev, speedUnits: event.target.value };
                })
              }
            >
              <option value="MPH">MPH</option>
              <option value="KPH">KPH</option>
            </select>
          </label>
          <label>
            Temperature Units
            <select
              value={selections.tempUnits}
              onChange={(event) =>
                setSelections((prev) => {
                  setIsProcessed(false);
                  setProcessedLabel('');
                  setHasDownloaded(false);
                  return { ...prev, tempUnits: event.target.value };
                })
              }
            >
              <option value="F">Fahrenheit</option>
              <option value="C">Celsius</option>
            </select>
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={selections.odometerCorrection}
              onChange={(event) =>
                setSelections((prev) => {
                  setIsProcessed(false);
                  setProcessedLabel('');
                  setHasDownloaded(false);
                  return { ...prev, odometerCorrection: event.target.checked };
                })
              }
            />
            Correct odometer mileage (Proof required)
          </label>
        </div>
      </section>

      {upload && upload.alreadyPaid && (
        <div className="download-wrapper">
          <span className="paid-badge">Paid</span>
        </div>
      )}

      <section className="eeprom-settings">
        <h3>Available Settings</h3>
        <ul className="settings-list">
          <li>Switch between F1 and gated transmission</li>
          <li>Switch between MPH and KPH</li>
          <li>Switch between Celsius and Fahrenheit</li>
          <li>
            Correct odometer mileage
            <span
              className="info-asterisk"
              title="Proof required: You must submit documentation showing why this change is needed."
            >
              *
            </span>
          </li>
        </ul>
      </section>

      <section className="eeprom-viewer">
        <h3>EEPROM Preview</h3>
        {vin && (
          <div className="vin-stack">
            <div
              className={`vin-banner ${isVinValid ? '' : 'vin-banner-invalid'}`}
            >
              {isVinValid ? 'VIN detected:' : 'Invalid VIN detected:'}
              <span>{vin}</span>
            </div>
            {isVinValid && vinYear && (
              <div className="vin-banner">
                Year detected: <span>{vinYear}</span>
              </div>
            )}
            {isVinValid && vinModel && (
              <div className="vin-banner">
                Model detected: <span>{vinModel}</span>
              </div>
            )}
            {isVinValid && vinPlant && (
              <div className="vin-banner">
                Assembly plant: <span>{vinPlant}</span>
              </div>
            )}
            {isVinValid && vinModel && vinModel.startsWith('360') && vinYear360 && (
              <div className="vin-banner">
                360 model year: <span>{vinYear360}</span>
              </div>
            )}
          </div>
        )}
        {!bytes && <p>Upload a binary EEPROM file to preview it here.</p>}
        {bytes && (
          <>
            <div className="eeprom-file-name">{fileName}</div>
            <div className="hex-view">
              <div className="hex-view-header">
                <span>Offset</span>
                <span>Hex</span>
                <span>ASCII</span>
              </div>
              <div className="hex-view-body">
                {rows.map((row) => (
                  <div key={row.offset} className="hex-row">
                    <span className="hex-offset">{row.offset}</span>
                    <span className="hex-bytes">
                      {row.hexBytes.map((value, index) => (
                        <span key={`${row.offset}-${index}`} className="hex-byte">
                          {value}
                        </span>
                      ))}
                    </span>
                    <span className="hex-ascii">
                      {row.ascii.split('').map((value, index) => (
                        <span
                          key={`${row.offset}-ascii-${index}`}
                          className="hex-ascii-char"
                        >
                          {value}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default F430Cluster;
